import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Globe, Loader2, AlertCircle, History } from 'lucide-react';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json';
import { githubGist } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import { useMetadata } from '../utils/metadata';
import { CopyButton } from '../components/common/CopyButton';
import { CopyAllButton } from '../components/common/CopyAllButton';

SyntaxHighlighter.registerLanguage('json', json);

interface HeaderResponse {
  [key: string]: string | string[];
}

function HttpHeaderViewer() {
  useMetadata({
    title: 'HTTP Header Viewer',
    description: 'Free online HTTP header viewer tool. Inspect HTTP response headers from any URL. Support for GET, POST, HEAD requests with custom User-Agent settings.',
    keywords: ['http headers', 'header viewer', 'http inspector', 'response headers', 'http debugging', 'web development tools', 'http request']
  });
  const [url, setUrl] = useState('');
  const [method, setMethod] = useState('GET');
  const [userAgent, setUserAgent] = useState(
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36'
  );
  const [loading, setLoading] = useState(false);
  const [headers, setHeaders] = useState<HeaderResponse | null>(null);
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside suggestions dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Initialize suggestions with common URLs and previously used ones
  useEffect(() => {
    const commonUrls = [
      'https://google.com',
      'https://github.com',
      'https://cloudflare.com',
      'https://microsoft.com',
      'https://amazon.com',
      'https://facebook.com',
      'https://twitter.com',
      'https://reddit.com'
    ];

    // Get previously used URLs from localStorage
    const storedUrls = localStorage.getItem('headerViewerHistory');
    const previousUrls = storedUrls ? JSON.parse(storedUrls) : [];

    // Combine user history with common URLs and take only the first 10 unique items
    const allUrls = [...new Set([...previousUrls, ...commonUrls])].slice(0, 10);
    setSuggestions(allUrls);
  }, []);

  // Store URL in localStorage after successful fetch
  const storeUrlInHistory = useCallback((url: string) => {
    const storedUrls = localStorage.getItem('headerViewerHistory');
    const previousUrls = storedUrls ? JSON.parse(storedUrls) : [];
    
    // Add new URL to the start of the array and remove duplicates
    const updatedUrls = [url, ...previousUrls.filter((u: string) => u !== url)].slice(0, 10);
    
    localStorage.setItem('headerViewerHistory', JSON.stringify(updatedUrls));
    // Combine with common URLs while maintaining the 10 item limit
    const commonUrls = [
      'https://google.com',
      'https://github.com',
      'https://cloudflare.com',
      'https://microsoft.com',
      'https://amazon.com',
      'https://facebook.com',
      'https://twitter.com',
      'https://reddit.com'
    ];
    const allUrls = [...new Set([...updatedUrls, ...commonUrls])].slice(0, 10);
    setSuggestions(allUrls);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!url) {
      setError('Please enter a URL');
      return;
    }

    try {
      // Validate URL format
      const parsedUrl = new URL(url);
      
      // Check for proxyscrape.com URLs
      if (parsedUrl.hostname.includes('proxyscrape.com')) {
        setError('URLs containing proxyscrape.com are not allowed');
        return;
      }
    } catch {
      setError('Please enter a valid URL including http:// or https://');
      return;
    }

    setLoading(true);
    setError('');
    setHeaders(null);

    try {
      const response = await fetch('https://api.proxyscrape.com/headerproxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url, method, userAgent }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch headers');
      }

      const data = await response.json();
      setHeaders(data);
      storeUrlInHistory(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch headers');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-4">HTTP Header Viewer</h1>
      <p className="text-gray-600 mb-8">
        View HTTP headers returned by any URL. Supports GET, POST, and HEAD requests with custom User-Agent settings.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-medium text-gray-900">Input</h2>
              <CopyButton text={url} />
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1">
                  URL
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit(e);
                      }
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    placeholder="https://example.com"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                    autoComplete="off"
                    required
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <div 
                      ref={suggestionsRef}
                      className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg border border-gray-200"
                    >
                      <div className="py-1">
                        <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100 flex items-center">
                          <History className="w-3 h-3 mr-1" />
                          Recent & Common URLs
                        </div>
                        {suggestions.map((suggestion, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              setUrl(suggestion);
                              setShowSuggestions(false);
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="method" className="block text-sm font-medium text-gray-700 mb-1">
                    Method
                  </label>
                  <select
                    id="method"
                    value={method}
                    onChange={(e) => setMethod(e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="HEAD">HEAD</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="userAgent" className="block text-sm font-medium text-gray-700 mb-1">
                    User-Agent
                  </label>
                  <input
                    type="text"
                    id="userAgent"
                    value={userAgent}
                    onChange={(e) => setUserAgent(e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Globe className="h-4 w-4 mr-2" />
                  )}
                  Fetch Headers
                </button>
              </div>
            </form>
          </div>
        </div>

        <div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-lg font-medium text-gray-900">Response Headers</h2>
              {headers && (
                <CopyAllButton 
                  items={[JSON.stringify(headers, null, 2)]} 
                  separator="" 
                  label="Copy JSON"
                />
              )}
            </div>
            {error ? (
              <div className="flex items-center space-x-2 text-red-600 p-4 bg-red-50 rounded-md">
                <AlertCircle className="h-5 w-5" />
                <span>{error}</span>
              </div>
            ) : (
              <SyntaxHighlighter
                language="json"
                style={githubGist}
                customStyle={{
                  borderRadius: '0.375rem',
                  maxHeight: '400px',
                }}
              >
                {headers ? JSON.stringify(headers, null, 2) : ''}
              </SyntaxHighlighter>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default HttpHeaderViewer;
